// The 10 demo scripts for the SoftBank + KDDI corp pitch.
// Each compiles via buildDemoScript() to a timed frame stream.
// Carrier-aware fields use `{{carrier.field}}` tokens substituted
// at play-time by compileDemoScript().
//
// Each demo lands all three endings: receipt → twin_update → pivot.

import { buildDemoScript, type DemoScript } from "./demoScript";


// ── KDDI Weekend Fun (MVP-A entry for KDDI / IP BANK demo) ────────


const kddiWeekendFun: DemoScript = buildDemoScript({
  meta: {
    id: "kddi-weekend-fun",
    title: "Weekend Fun in Tokyo",
    subtitle: "Event → transport → lunch coupon → Ponta-optimized payment",
    carrierFit: "kddi",
    userPrompt: "I want to do something fun this weekend",
    consumerId: "demo_consumer",
    turnId: "demo_t_kddi_weekend",
    dealId: "demo_d_kddi_weekend",
    totalSeconds: 80,
    killerFeatures: [
      "Event discovery (IP BANK)",
      "Cross-vertical: event + transport + lunch",
      "Ponta + au points stacked at payment",
      "Light, cost-effective tone",
    ],
    endings: {
      receipt: {
        receipt_id: "rcp_kddi_wknd_4e2a91c",
        action_summary: "Sealed Saturday: rooftop live show + train + udon lunch — ¥3,720 net after Ponta + au points",
        midnight_anchor_hex: "4e2a91cb2459e2af2049c22dc8c8fa9c",
        rationale_steps: [
          "Twin read · you usually pick casual + outdoors when budget-leaning",
          "IP BANK feed surfaced 3 free-or-cheap live events Saturday",
          "Negotiator paired the rooftop show with a 4-min walk udon spot that has a Ponta coupon (Lawson collab)",
          "Train chosen via Yamanote loop · ¥420 (cheapest viable route)",
          "Payment stacked: 200 Ponta (lunch coupon) + 800 au points + ¥3,720 net",
        ],
        alternatives_considered: [
          "Roppongi gallery opening · ¥1,500 entry (over the casual-cheap pattern)",
          "Shibuya stand-up show · ¥2,800 (no Ponta partner)",
          "Akihabara arcade afternoon · matched casual-cheap but no food bundle",
        ],
        jfsa_csv_preview:
          "transaction_id,transaction_datetime_jst,transaction_type,counterparty_name,denomination,amount,amount_jpy_equivalent,midnight_anchor_hash,settlement_adapter_id,tax_category\nrcp_kddi_wknd_4e2a91c,2026-05-23T16:00:00+09:00,deal_sealed,RooftopLive Ebisu + Udon Bunsuke,YEN,-3720,-3720,4e2a91cb2459e2af2049c22dc8c8fa9c,coincheck_spot,consumption_expense",
      },
      twinUpdate: {
        category: "weekend.pattern",
        key: "casual_cheap_with_ponta",
        old_value: "(no recorded weekend pattern)",
        new_value: "outdoors-leaning · under ¥5k · Ponta-eligible lunch bundles preferred",
        reason: "First weekend-fun plan via Lifestyle anchors the pattern Aristotle will reuse next Saturday",
        confidence_change: 0.38,
      },
      pivot: {
        headline: "Stack the rest of Saturday?",
        suggestions: [
          { vertical: "entertainment", description: "Movie ticket after the show · Toho Hibiya · Ponta-stacked", ctaLabel: "Add evening movie" },
          { vertical: "ecommerce", description: "Lawson convenience stop · 200 Ponta off Premium roll cake", ctaLabel: "Pin to Lawson run" },
          { vertical: "travel.transport", description: "Pre-book the last Yamanote loop back at 23:11", ctaLabel: "Lock train" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("I want to do something fun this weekend");
    s.nodeState("triton", "active");
    s.edgeFire("triton", "your_twin", "twin.read");
    s.nodeState("your_twin", "active");
    s.agentSay("Your Twin",
      "Got it — Saturday's open. You tend to lean casual and outdoors, and you've been keeping it under ¥5k lately. Let me see what's around.",
      "your_twin");
    s.edgeFire("your_twin", "aristotle", "feed.events");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "IP BANK has three events Saturday that fit you — a rooftop live show in Ebisu, a Shibuya stand-up night, and a free Akihabara arcade pop-up. Want me to dig in?",
      "aristotle");
    s.agentSay("Your Twin",
      "Yeah, ask each one what they've got. And if there's a Ponta-partnered lunch nearby, even better — you've got 4,200 Ponta sitting unused.",
      "your_twin");
    s.edgeFire("your_twin", "pact_broker", "broadcast.weekend");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "Sending the brief to the three event organizers + nearby Ponta-partnered lunch spots + Yamanote-line transport. Bids coming in.",
      "broker");
    s.pactBroadcast("entertainment.weekend", 6);
    s.agentSay("RooftopLive Ebisu",
      "Free entry Saturday 4pm — outdoor jazz trio, rooftop venue, 90 minutes. Capacity 80, still open.",
      "provider");
    s.pactBid("rooftop_ebisu", "RooftopLive Ebisu", 0);
    s.agentSay("Shibuya Stand-Up",
      "¥2,800 cover, 90-min show, indoors. No Ponta partner.",
      "provider");
    s.pactBid("shibuya_standup", "Shibuya Stand-Up", 2_800);
    s.agentSay("Akihabara Arcade Pop-Up",
      "Free entry all day, retro arcade machines. No food bundle.",
      "provider");
    s.pactBid("akiba_arcade", "Akihabara Arcade Pop-Up", 0);
    s.agentSay("Udon Bunsuke (Ebisu)",
      "¥1,300 udon set lunch · 4-minute walk from RooftopLive · we honor 200 Ponta off through our Lawson collab.",
      "provider");
    s.pactBid("udon_bunsuke", "Udon Bunsuke", 1_300);
    s.agentSay("Tachigui Soba (Akihabara)",
      "¥600 standing soba near the arcade · no Ponta tie-up.",
      "provider");
    s.pactBid("tachigui_soba", "Tachigui Soba", 600);
    s.agentSay("Yamanote Loop · transport agent",
      "Ebisu via JR Yamanote · 22 min · ¥220 each way. Akihabara via JR · 31 min · ¥210.",
      "provider");
    s.pactBid("jr_yamanote", "JR Yamanote", 420);
    s.agentSay("Your Twin",
      "Shibuya stand-up is over budget at ¥2,800. Akihabara is fine but no food bundle. Rooftop + Udon Bunsuke gives us free entry + a Ponta-eligible lunch — that's the play.",
      "your_twin");
    s.pactDrop("shibuya_standup", "over_budget");
    s.pactDrop("akiba_arcade", "no_lunch_bundle");
    s.pactDrop("tachigui_soba", "no_ponta_partner");
    s.agentSay("Negotiator",
      "Bundling RooftopLive (free) + Udon Bunsuke (¥1,300) + Yamanote round-trip (¥420). Gross ¥1,720. Applying 200 Ponta from Lawson coupon + 800 au points → net ¥720 out of your au PAY balance.",
      "negotiator");
    s.nodeState("negotiator", "active");
    s.agentSay("Your Twin",
      "Wait — let me re-check. ¥1,720 gross minus 200 Ponta is ¥1,520, then 800 au points is ¥720. But you wanted to keep some flex for an evening drink. Lock it at ¥720 net.",
      "your_twin");
    s.pactSealed("kddi_weekend_bundle", "Rooftop + Udon + Yamanote", 720);
    s.nodeState("negotiator", "done", "Bundle ¥720 net");
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("au PAY · Coincheck Wallet",
      "Settled ¥720 — applied 200 Ponta (now 4,000 left) + 800 au points (now 5,600 left). Tickets and lunch reservation are in your wallet under Saturday's tab.",
      "settlement");
    s.nodeState("settlement", "done", "Net ¥720");
    s.nodeState("midnight", "done", "⛓ 4e2a91c…");
    s.nodeState("triton", "done");
    s.assistantDone("Saturday locked: rooftop jazz at 4pm, udon at 6, Yamanote home. ¥720 net after Ponta + au points. Want to stack a Toho movie after?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 1. Agent-to-Agent Live Marketplace ────────────────────────────


const agentToAgentLive: DemoScript = buildDemoScript({
  meta: {
    id: "agent-to-agent-live",
    title: "Agent-to-Agent Live Marketplace",
    subtitle: "Your agent vs 5 provider agents · bid → counter-bid → counter-bid → seal",
    carrierFit: "both",
    userPrompt: "Sushi tonight in Roppongi, 2 seats around 19:30, ≤¥20,000 — best value",
    consumerId: "demo_consumer",
    turnId: "demo_t_a2a",
    dealId: "demo_d_a2a",
    totalSeconds: 70,
    killerFeatures: ["PACT agent-to-agent", "Live counter-bidding", "5 provider agents compete"],
    endings: {
      receipt: {
        receipt_id: "rcp_a2a_3a90fe1",
        action_summary: "Sealed Sushi Tama · 19:30 for 2 · ¥17,500 net (+ free dessert)",
        midnight_anchor_hex: "3a90fe18261510dc8c8fa9c7df7b0a45",
        rationale_steps: [
          "Triton dispatched intent to PACT broker · 5 provider agents responded",
          "Round 1 bids · ¥24k/¥19.5k/¥18k/¥22k/¥19k · 2 dropped over-budget",
          "Round 2 best-and-final · 3 agents counter-bid: ¥18.5k/¥17.5k/¥17.8k",
          "Round 3 sweeteners · Tama added free dessert · Tora offered sake pairing",
          "Negotiator scored: price 40% · reliability 30% · chef rating 30% → Tama won",
          "Settlement via {{carrier.settlementBrand}} · Midnight anchored both legs",
        ],
        alternatives_considered: [
          "Sushi Saito · ¥24k · over-budget round 1",
          "Sushi Yoshi · ¥22k · over-budget round 1",
          "Sushi Iwa · ¥18.5k round 2 · lost on reliability",
          "Sushi Tora · ¥17.8k + sake pairing · lost on chef rating",
        ],
        jfsa_csv_preview:
          "transaction_id,transaction_datetime_jst,transaction_type,counterparty_name,denomination,amount,amount_jpy_equivalent,midnight_anchor_hash,settlement_adapter_id,tax_category\nrcp_a2a_3a90fe1,2026-05-18T19:30:00+09:00,deal_sealed,Sushi Tama,{{carrier.primaryDenomination}},-17500,-17500,3a90fe18261510dc8c8fa9c7df7b0a45,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "negotiation.tactic",
        key: "best_and_final_responsiveness",
        old_value: "(no agent-bidding history)",
        new_value: "responds to best-and-final · 12% avg price reduction · prefers value sweeteners over raw price cuts",
        reason: "First live A2A negotiation seeds the Twin's bidding strategy preference",
        confidence_change: 0.42,
      },
      pivot: {
        headline: "Want your agent to keep negotiating?",
        suggestions: [
          { vertical: "drinks", description: "Bar Trench · cocktails 21:00 · agent will broker bids on arrival", ctaLabel: "Dispatch agent" },
          { vertical: "travel.car", description: "Black car home · 3 driver agents will bid for the ride", ctaLabel: "Start auction" },
          { vertical: "ecommerce.recurring", description: "Lock the A2A bidding pattern · auto-run for every sushi search", ctaLabel: "Save tactic" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("Sushi tonight in Roppongi, 2 seats around 19:30, ≤¥20,000 — best value");
    s.nodeState("triton", "active");
    s.agentSay("Your Twin",
      "Got it — sushi tonight, two seats around 7:30, keeping it under ¥20k, going for value. Let me check who's free.",
      "your_twin");
    s.nodeState("your_twin", "active");
    s.edgeFire("your_twin", "pact_broker", "broadcast.intent");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "Five Roppongi spots have seats open tonight — let me put your name out there.",
      "broker");
    s.pactBroadcast("food.sushi.value", 5);
    s.agentSay("Sushi Saito",
      "Hey, would love to have you! Our omakase tonight runs ¥24k a head — two seats at 7:30 are yours if you want them.",
      "provider");
    s.pactBid("saito", "Sushi Saito", 24_000);
    s.agentSay("Sushi Iwa",
      "Hi! We can do ¥19,500 — fourteen-course tasting, the chef's been on fire lately. 7:30 works for us.",
      "provider");
    s.pactBid("iwa", "Sushi Iwa", 19_500);
    s.agentSay("Sushi Tama",
      "Oh we'd love to have you! ¥18,000 for twelve courses — our chef trained under one of Saito's old apprentices, you'd never guess from the price.",
      "provider");
    s.pactBid("tama", "Sushi Tama", 18_000);
    s.agentSay("Sushi Yoshi",
      "Hi there! ¥22k a head for fifteen seasonal courses, two seats at 7:30 — we'd love to feed you.",
      "provider");
    s.pactBid("yoshi", "Sushi Yoshi", 22_000);
    s.agentSay("Sushi Tora",
      "Hey! ¥19k a head, thirteen courses, very traditional — totally classic spot. Two seats at 7:30, all yours.",
      "provider");
    s.pactBid("tora", "Sushi Tora", 19_000);
    s.agentSay("Marketplace",
      "Aw, Saito and Yoshi are sweet but they're over your number. I'll let them down nicely.",
      "broker");
    s.pactDrop("saito", "over_budget");
    s.pactDrop("yoshi", "over_budget");
    s.agentSay("Marketplace",
      "OK so we've got three of you left — any of you want to give it one more shot? Best you can do?",
      "broker");
    s.edgeFire("pact_broker", "negotiator", "open.bnf");
    s.nodeState("negotiator", "active");
    s.agentSay("Sushi Iwa",
      "OK ok — I can do ¥18,500. That's me being generous, but we really want you in here.",
      "provider");
    s.pactBid("iwa", "Sushi Iwa", 18_500);
    s.agentSay("Sushi Tama",
      "¥17,500 — that's our best, honest. We really really want this one.",
      "provider");
    s.pactBid("tama", "Sushi Tama", 17_500);
    s.agentSay("Sushi Tora",
      "¥17,800 is as low as I can go without skipping courses, and I'm not gonna do that to you.",
      "provider");
    s.pactBid("tora", "Sushi Tora", 17_800);
    s.agentSay("Negotiator",
      "Hmm, Tama and Tora are basically tied when I factor in everything. Wanna see if either of them can sweeten it?",
      "negotiator");
    s.agentSay("Marketplace",
      "Tama, Tora — anything else you can throw in? It's between you two.",
      "broker");
    s.edgeFire("pact_broker", "negotiator", "open.sweetener");
    s.agentSay("Sushi Tama",
      "OK how about this — chef'll send out a seasonal dessert on the house. That's a ¥800 dish, our gift.",
      "provider");
    s.pactBid("tama", "Sushi Tama · +dessert", 17_500);
    s.agentSay("Sushi Tora",
      "I'll add the sake pairing for just ¥300 — usually ¥500. Hard to beat that.",
      "provider");
    s.pactBid("tora", "Sushi Tora · +sake", 17_800);
    s.agentSay("Your Twin",
      "Honestly? The free dessert is worth more than the sake deal, and Tama's not asking for a single yen more. That's the one.",
      "your_twin");
    s.agentSay("Negotiator",
      "Yeah, going with Tama. Let me lock it in for you.",
      "negotiator");
    s.pactDrop("iwa", "outscored");
    s.pactDrop("tora", "outscored_on_value");
    s.pactSealed("tama", "Sushi Tama", 17_500);
    s.nodeState("negotiator", "done", "Tama ¥17,500");
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Yep — Midnight just settled ¥17,500 from your balance. Tama's been notified, table's locked for 7:30 🍣",
      "settlement");
    s.nodeState("settlement", "done", "Paid ¥17,500");
    s.nodeState("midnight", "done", "⛓ 3a90fe18…");
    s.nodeState("pact_broker", "done");
    s.nodeState("triton", "done");
    s.assistantDone("Tama at 7:30, ¥17,500 with a dessert on the house. Saved you a clean ¥6,500 from the top offer 🍣");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 2. Anniversary in Minato ──────────────────────────────────────


const anniversaryMinato: DemoScript = buildDemoScript({
  meta: {
    id: "anniversary-minato",
    title: "Anniversary in Minato",
    subtitle: "Personal Scout · Provable Surprise · {{carrier.settlementBrand}} settles",
    carrierFit: "both",
    userPrompt: "French restaurant in Minato-ku for our anniversary — duck confit + cheese course + Château Latour on the list, ≤ ¥40,000 for two",
    consumerId: "demo_consumer",
    turnId: "demo_t_anniv",
    dealId: "demo_d_anniv",
    totalSeconds: 65,
    killerFeatures: ["UC-7 Provable Surprise", "UC-4 Live PACT", "UC-1 Receipts", "Loyalty math"],
    endings: {
      receipt: {
        receipt_id: "rcp_anniv_3f8c93a",
        action_summary: "Sealed table for two — L'Ambassade @ 19:30, Latour by the glass",
        midnight_anchor_hex: "7a90c41918261510dc8c8fa9c7df7b0a",
        rationale_steps: [
          "Twin pattern · anniversary dinners pull intimate-setting + complimentary-dessert preference",
          "Crawler found 14 French in Minato → 7 with duck confit → 3 with Latour",
          "Provider Verifier 4.7/5 reliability on L'Ambassade across last 90 days",
          "Slot window match · 19:30 within stated 19:00–21:00 range",
        ],
        alternatives_considered: [
          "Crony Roppongi · Latour ¥85k bottle exceeded budget",
          "Restaurant Quintessence · fully booked anniversary date",
          "Florilege Aoyama · cheese course incomplete",
        ],
        jfsa_csv_preview:
          "transaction_id,transaction_datetime_jst,transaction_type,counterparty_name,denomination,amount,amount_jpy_equivalent,midnight_anchor_hash,settlement_adapter_id,tax_category\nrcp_anniv_3f8c93a,2026-05-18T19:30:00+09:00,deal_sealed,L'Ambassade,{{carrier.primaryDenomination}},-33000,-33000,7a90c41918261510dc8c8fa9c7df7b0a,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "dining.occasion",
        key: "anniversary",
        old_value: "(none recorded)",
        new_value: "intimate · French · Latour-tier wine · 19:30 sweet spot",
        reason: "Sealed deal at L'Ambassade reinforces the anniversary-pattern signal Aristotle started tracking last year",
        confidence_change: 0.34,
      },
      pivot: {
        headline: "Want me to book the whole evening?",
        suggestions: [
          { vertical: "culture", description: "Mori Art Museum · new exhibition · open til 22:00 · 4 min walk", ctaLabel: "Add tickets" },
          { vertical: "drinks", description: "Gen Yamamoto · omakase cocktails · 8 seats · 5 min walk", ctaLabel: "Reserve nightcap" },
          { vertical: "travel.car", description: "Black-car back to your place at 22:30", ctaLabel: "Schedule car" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("French restaurant in Minato-ku for our anniversary — duck confit + cheese course + Château Latour ≤¥40k for two");
    s.nodeState("triton", "active");
    s.nodeState("your_twin", "active");
    s.nodeState("partner_twin", "active");
    s.agentSay("Your Twin",
      "Got it — French in Minato, anniversary, duck confit, cheese course, Latour, ¥40k ceiling for two. Let me loop in your partner's Twin first — partner-side, anything I should weight extra?",
      "your_twin");
    s.agentSay("Partner Twin",
      "The wine, hands down. Duck confit's nice but honestly, no Latour, no dice. They'd rather a tiny bistro with the right bottle than fine dining without it.",
      "partner_twin");
    s.agentSay("Your Twin",
      "Got it, perfect. So — French in Minato, cheese trolley, somewhere with Latour, keep it under ¥40k. Oh and I'll skip the shellfish-forward places, you've never been into that.",
      "your_twin");
    s.agentSay("Partner Twin",
      "One more thing — let's aim for a 7:30 start? They love being home by ten. You know how they are.",
      "partner_twin");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "Hey y'all — fun fact, this'd be your fourth anniversary dinner together. The last three were all Bordeaux + cheese + 7:30. I'd say that's officially your tradition now 🥂",
      "aristotle");
    s.edgeFire("aristotle", "pact_broker", "broadcast");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "OK let me put the word out to the fourteen French spots in Minato. Four said they can host you!",
      "broker");
    s.pactBroadcast("food.fr", 14);
    s.agentSay("Crony Roppongi",
      "Bonsoir! Yes — 2010 Latour by the bottle, duck confit, our full cheese trolley. We're at ¥85k for two, would love to have you.",
      "provider");
    s.pactBid("crony_rop", "Crony Roppongi", 85_000);
    s.agentSay("L'Ambassade",
      "Bonsoir — yes 7:30 is perfect. ¥38k for two: duck confit, the whole cheese trolley, and we'll pour Latour 2005 by the glass. We'd love to host an anniversary 💛",
      "provider");
    s.pactBid("lambassade", "L'Ambassade", 38_000);
    s.agentSay("Restaurant Quintessence",
      "Oh, we'd absolutely love to but we're fully booked that night — I'm so sorry! Please come another time?",
      "provider");
    s.pactBid("quintessence", "Restaurant Quintessence", 38_000, 200);
    s.pactDrop("quintessence", "fully_booked");
    s.agentSay("Florilège Aoyama",
      "We can do ¥34k — duck confit yes! But heads up, our cheese course is a curated selection of three, no trolley. Hope that's OK?",
      "provider");
    s.pactBid("florilege", "Florilege Aoyama", 34_000);
    s.agentSay("Partner Twin",
      "Aw, no — the trolley's half the fun for them, picking their own. Florilège is out.",
      "partner_twin");
    s.pactDrop("florilege", "no_trolley");
    s.agentSay("Your Twin",
      "And Crony's a stretch at ¥85k. So L'Ambassade it is — they're checking every box.",
      "your_twin");
    s.pactDrop("crony_rop", "over_budget");
    s.nodeState("negotiator", "active");
    s.edgeFire("pact_broker", "negotiator", "rank");
    s.agentSay("Negotiator",
      "L'Ambassade's the one. Let me see if they wanna do something a little extra since it's an anniversary 💫",
      "negotiator");
    s.agentSay("L'Ambassade",
      "Oh of course! For your anniversary — let's call it ¥33k, we'll send out a complimentary toast when you arrive, and you'll have the full trolley. Sound good?",
      "provider");
    s.pactSealed("lambassade", "L'Ambassade", 33_000);
    s.nodeState("negotiator", "done", "L'Ambassade ¥33k");
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Done — Midnight settled it. Your table's locked for 7:30 💕",
      "settlement");
    s.nodeState("settlement", "done", "Paid ¥28,000");
    s.nodeState("midnight", "done", "⛓ 7a90c419…");
    s.nodeState("triton", "done");
    s.assistantDone("L'Ambassade at 7:30 — duck confit, cheese trolley, Latour by the glass, and an anniversary toast on the house. ¥33k. Have the best night 💛");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 2. Kyoto Weekend with Partner ─────────────────────────────────


const kyotoPartnerWeekend: DemoScript = buildDemoScript({
  meta: {
    id: "kyoto-partner-weekend",
    title: "Kyoto Weekend with Partner",
    subtitle: "Partner-Fit · two Twins negotiate · atomic multi-deal seal",
    carrierFit: "both",
    userPrompt: "Kyoto next month with my partner — three days, something relaxing",
    consumerId: "demo_consumer",
    turnId: "demo_t_kyoto",
    dealId: "demo_d_kyoto",
    totalSeconds: 75,
    killerFeatures: ["UC-6 Partner-Fit", "UC-3 Twin Mirror", "Atomic multi-deal seal", "Cross-vertical"],
    endings: {
      receipt: {
        receipt_id: "rcp_kyoto_b2459e2",
        action_summary: "6 deals sealed atomically — Aoyama Stay + 2 dinners + couples spa + 2 transfers",
        midnight_anchor_hex: "b2459e2af2049c22dc8c8fa9c7df7b0a",
        rationale_steps: [
          "Twin A · ryokan + onsen + quiet rooms",
          "Twin B · modern + shellfish-free + city views",
          "Aristotle blend · boutique with onsen on top floor + city view from room",
          "Multi-vendor atomic lock · one or none, all sealed in single Midnight transaction",
        ],
        alternatives_considered: [
          "Tawaraya Inn · onsen yes, modern style no",
          "Park Hyatt Kyoto · modern yes, onsen on lower floor (no view)",
          "Sowaka · partial blend but no shellfish-free menu",
        ],
        jfsa_csv_preview:
          "rcp_kyoto_b2459e2,2026-06-14T15:00:00+09:00,deal_sealed,Aoyama Stay Kyoto,{{carrier.primaryDenomination}},-168000,-168000,b2459e2af2049c22dc8c8fa9c7df7b0a,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "travel.couple",
        key: "kyoto",
        old_value: "(empty)",
        new_value: "boutique-not-ryokan · onsen non-negotiable both · shellfish-free menu always",
        reason: "First couple's trip to Kyoto records the blend Aristotle will reuse next time",
        confidence_change: 0.4,
      },
      pivot: {
        headline: "While you're in Kyoto?",
        suggestions: [
          { vertical: "wellness", description: "Tea ceremony at Camellia · matches your last 'cultural' rating", ctaLabel: "Add 90-min slot" },
          { vertical: "food.local", description: "Kichi Kichi omurice · queues at 17:00 · reserve walk-up slot", ctaLabel: "Hold slot" },
          { vertical: "shopping", description: "Nishiki market morning walk · pre-coffee, beat crowds", ctaLabel: "Add to itinerary" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("Kyoto next month with my partner — three days, something relaxing");
    s.nodeState("triton", "active");
    s.nodeState("your_twin", "active");
    s.nodeState("partner_twin", "active");
    s.agentSay("Your Twin",
      "Three days in Kyoto, something relaxing. My take — traditional ryokan, onsen, tatami rooms. What's your partner thinking?",
      "your_twin");
    s.agentSay("Partner Twin",
      "Honestly, almost the opposite. They like modern places with a view. And they have a shellfish allergy — not a preference, an allergy — so the restaurant matters.",
      "partner_twin");
    s.agentSay("Your Twin",
      "OK, two hard rules from your side: modern feel, no shellfish anywhere on the menu. I can flex on the ryokan part — what mine really wants is onsen access, not the building style.",
      "your_twin");
    s.agentSay("Partner Twin",
      "Good. Then both of you want onsen. If we can find one on the top floor with a city view, that'd be perfect for mine. Budget — they're up to ¥180,000 for the three nights.",
      "partner_twin");
    s.agentSay("Your Twin",
      "Mine likes to stay around ¥160,000. Splitting the difference at ¥170,000 feels fair. Let me ask the boutique hotels.",
      "your_twin");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "Noting this for next time: boutique, top-floor onsen, shellfish-free, around ¥170k. First couples trip to Kyoto — I'll reuse this as your default if you go again.",
      "aristotle");
    s.edgeFire("aristotle", "pact_broker", "broadcast.multi_vendor");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "I'll check the twelve boutique-and-ryokan options in Kyoto for those dates. Four can host you.",
      "broker");
    s.pactBroadcast("travel.couple", 12);
    s.agentSay("Tawaraya Inn",
      "Welcome — we're a classical ryokan with private onsen. ¥220,000 for three nights, full traditional service.",
      "provider");
    s.pactBid("tawaraya", "Tawaraya Inn", 220_000);
    s.agentSay("Aoyama Stay Kyoto",
      "We're a boutique hotel with the onsen on our top floor — city view included. ¥168,000 for three nights, and we'll prep a fully shellfish-free menu on request.",
      "provider");
    s.pactBid("aoyama_stay", "Aoyama Stay Kyoto", 168_000);
    s.agentSay("Park Hyatt Kyoto",
      "Modern, definitely — our onsen is lovely, though it's on a lower floor so no city view from inside. ¥195,000 for three nights.",
      "provider");
    s.pactBid("park_hyatt", "Park Hyatt Kyoto", 195_000);
    s.agentSay("Sowaka",
      "Boutique with onsen — ¥142,000 for three nights. Honest answer on shellfish though: our kitchen isn't able to fully isolate it. I'd be cautious if it's an allergy.",
      "provider");
    s.pactBid("sowaka", "Sowaka", 142_000);
    s.agentSay("Partner Twin",
      "Sowaka's out — an allergy isn't something to gamble on. And Tawaraya's too traditional for mine.",
      "partner_twin");
    s.pactDrop("sowaka", "shellfish_risk");
    s.pactDrop("tawaraya", "too_traditional");
    s.agentSay("Your Twin",
      "Aoyama checks every box — and it's ¥27,000 less than Park Hyatt. Let's go with Aoyama.",
      "your_twin");
    s.nodeState("negotiator", "active");
    s.edgeFire("pact_broker", "negotiator", "rank");
    s.pactSealed("aoyama_stay", "Aoyama Stay Kyoto", 168_000);
    s.nodeState("negotiator", "done", "Aoyama ¥168k");
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Deposit's gone through — ¥50k out of your balance, the remaining ¥118k waits til check-in. If anything breaks on their end, you get the deposit back.",
      "settlement");
    s.nodeState("settlement", "done", "Deposit paid");
    s.nodeState("midnight", "done", "⛓ b2459e2…");
    s.nodeState("triton", "done");
    s.assistantDone("6 sealed · while you're in Kyoto, want me to add tea ceremony + Nishiki walk?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 3. Family Hakone Holiday ──────────────────────────────────────


const familyHakone: DemoScript = buildDemoScript({
  meta: {
    id: "family-hakone",
    title: "Family Hakone Holiday",
    subtitle: "4-way Twin · Aristotle pattern · group payment split",
    carrierFit: "both",
    userPrompt: "Hakone weekend after next — all 4 of us",
    consumerId: "demo_consumer",
    turnId: "demo_t_hakone",
    dealId: "demo_d_hakone",
    totalSeconds: 70,
    killerFeatures: ["UC-6 Family Twin (4-way)", "Aristotle pattern recall", "Group payment split", "Self-executing refund"],
    endings: {
      receipt: {
        receipt_id: "rcp_hakone_d52b3d7",
        action_summary: "Sealed Hakone Yumoto resort · spa for parents · kayak for teens",
        midnight_anchor_hex: "d52b3d7af2049c22b2459e2a05fdba9e",
        rationale_steps: [
          "4 Twins · parents want late checkout + spa · teens want morning kayak + arcade",
          "Aristotle pattern · this family always splits mornings + reconvenes dinner",
          "Resort matches: spa + kayak rental + family-style dinner",
          "Group split locked: each PayPay charged proportional; one-cancellation = one-refund",
        ],
        alternatives_considered: [
          "Hakone Kowakien · no kayak rental",
          "Hyatt Regency · kayak only at extra cost · over budget when summed",
          "Tenseien · spa-only, no teen activities",
        ],
        jfsa_csv_preview:
          "rcp_hakone_d52b3d7,2026-05-31T15:00:00+09:00,deal_sealed,Hakone Yumoto Resort,{{carrier.primaryDenomination}},-92000,-92000,d52b3d7af2049c22b2459e2a05fdba9e,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "travel.family.pattern",
        key: "morning_split",
        old_value: "0.62 confidence (5 prior outings)",
        new_value: "0.78 confidence (6 prior outings) · pattern strengthens",
        reason: "Hakone booking confirms the pattern; Aristotle bumps confidence",
        confidence_change: 0.16,
      },
      pivot: {
        headline: "Add to the weekend?",
        suggestions: [
          { vertical: "travel.car", description: "Romance Car upgrade Shinjuku → Hakone · 2 cars saved last time", ctaLabel: "Upgrade tickets" },
          { vertical: "food", description: "Hakone-Yumoto onsen-tamago breakfast on Saturday morning", ctaLabel: "Reserve table for 4" },
          { vertical: "culture", description: "Hakone Open-Air Museum · all 4 ratings ≥4.5 last year", ctaLabel: "Add Sunday morning" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("Hakone weekend after next — all 4 of us");
    s.nodeState("triton", "active");
    // Four separate Twin nodes — one per family member, all in lane 1
    s.nodeState("your_twin", "active", "You");
    s.nodeState("partner_twin", "active", "Partner");
    s.nodeState("twin_3", "active", "Teen A");
    s.nodeState("twin_4", "active", "Teen B");
    s.agentSay("Your Twin",
      "Hakone for the four of you. Mine wants a slow morning — sleep in, then spa, then everyone together for dinner. What about your partner?",
      "your_twin");
    s.agentSay("Partner Twin",
      "Same on dinner, but mine likes a 6am yoga session before the rest of you wake up. Let's hear from the teens.",
      "partner_twin");
    s.agentSay("Teen A Twin",
      "Mine wants kayaking on the lake in the morning. Bored if it's only spas.",
      "family_twin");
    s.agentSay("Teen B Twin",
      "Mine wants the arcade and a ramen lunch. Not into kayaking.",
      "family_twin");
    s.agentSay("Your Twin",
      "OK — sounds like everyone splits up in the mornings and we meet for dinner around 6:30. We need a place with a spa, kayak rentals, an arcade nearby, and a family-style restaurant on site.",
      "your_twin");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "This is the sixth family trip where you've done morning-apart, dinner-together. It's a real pattern now — I'll plan future ones the same way unless you tell me otherwise.",
      "aristotle");
    s.edgeFire("aristotle", "pact_broker", "broadcast");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "I'll ask the eighteen Hakone resorts that fit a family of four. Four can take you.",
      "broker");
    s.pactBroadcast("travel.family", 18);
    s.agentSay("Hakone Kowakien",
      "¥88,000 for the four of you. We have the spa and a family restaurant, and the arcade's right next door. No kayak rentals though.",
      "provider");
    s.pactBid("kowakien", "Hakone Kowakien", 88_000);
    s.agentSay("Hakone Yumoto Resort",
      "¥92,000 — spa, kayak rentals on-site, arcade is a short walk, and we have a lakeside restaurant. Everything in one place.",
      "provider");
    s.pactBid("yumoto", "Hakone Yumoto Resort", 92_000);
    s.agentSay("Hyatt Regency Hakone",
      "¥124,000 — we have everything you asked for. Premium tier.",
      "provider");
    s.pactBid("hyatt_h", "Hyatt Regency Hakone", 124_000);
    s.agentSay("Tenseien",
      "¥76,000 — beautiful spa, but honestly nothing for the teens to do.",
      "provider");
    s.pactBid("tenseien", "Tenseien", 76_000);
    s.agentSay("Teen A Twin",
      "Tenseien's out — without the kayak there's nothing for mine.",
      "family_twin");
    s.pactDrop("tenseien", "no_teen_activities");
    s.agentSay("Teen B Twin",
      "Kowakien would actually work for mine — arcade's the thing I wanted.",
      "family_twin");
    s.agentSay("Teen A Twin",
      "Not for mine though. The kayak isn't optional. Sorry — Kowakien's out too.",
      "family_twin");
    s.pactDrop("kowakien", "no_kayak");
    s.agentSay("Your Twin",
      "Yumoto checks every box for all four of us and it's ¥32,000 less than Hyatt. Let's book Yumoto.",
      "your_twin");
    s.nodeState("negotiator", "active");
    s.pactSealed("yumoto", "Hakone Yumoto Resort", 92_000);
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "All four wallets settled ¥23k via Midnight. Everyone's confirmed 🏔️",
      "settlement");
    s.nodeState("settlement", "done", "Split paid");
    s.nodeState("midnight", "done", "⛓ d52b3d7…");
    s.nodeState("triton", "done");
    s.assistantDone("Yumoto's booked — spa, kayak, arcade, family dinner. ¥23,000 from each of you. Want me to add the Romance Car ride?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 4. Group Birthday Dinner ──────────────────────────────────────


const groupBirthdayDinner: DemoScript = buildDemoScript({
  meta: {
    id: "group-birthday-dinner",
    title: "Group Birthday Dinner",
    subtitle: "6-way Twin · scoped sharing · group payment split",
    carrierFit: "both",
    userPrompt: "Birthday dinner Friday for me + 5 friends",
    consumerId: "demo_consumer",
    turnId: "demo_t_bday",
    dealId: "demo_d_bday",
    totalSeconds: 60,
    killerFeatures: ["UC-6 (6-way Twin)", "Privacy Protocol scoped sharing", "Group payment split", "No group chat needed"],
    endings: {
      receipt: {
        receipt_id: "rcp_bday_70430d0",
        action_summary: "Sealed Den Roppongi · vegetarian + omakase + 6 covers Friday 20:00",
        midnight_anchor_hex: "70430d07a90c41918261510dc8c8fa9c",
        rationale_steps: [
          "6 Twins broadcast scoped prefs · 2 vegetarian, 1 shellfish allergy, ¥4k/head, Japanese-only, 'fun'",
          "Provider Verifier filtered 50 → 18 venues by reliability",
          "Negotiator ranked by Twin-fit + price + atmosphere score",
          "Settled at ¥3,800/head + 10% tax = ¥4,180/head, 6 splits charged simultaneously",
        ],
        alternatives_considered: [
          "Narisawa · over budget at ¥18k/head",
          "Yakiniku Toraji · no separate vegetarian menu",
          "Park Restaurant · 'fun' score 2.4/5 (too formal)",
        ],
        jfsa_csv_preview:
          "rcp_bday_70430d0,2026-05-23T20:00:00+09:00,deal_sealed,Den Roppongi,{{carrier.primaryDenomination}},-4180,-4180,70430d07a90c41918261510dc8c8fa9c,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "dining.group",
        key: "friend_group_fri",
        old_value: "(empty)",
        new_value: "vegetarian + omakase + lively · ≤¥4k/head sweet spot · 20:00 start",
        reason: "First group dinner via Lifestyle records the friend-group profile Aristotle will reuse",
        confidence_change: 0.31,
      },
      pivot: {
        headline: "Make it a night?",
        suggestions: [
          { vertical: "drinks", description: "Bar Trench cocktails after dinner · 5 min walk · books for 6", ctaLabel: "Reserve" },
          { vertical: "travel.car", description: "Single black car back to your place at 23:00", ctaLabel: "Schedule" },
          { vertical: "gift", description: "Cake from Patisserie SATSUKI · pre-order for Friday 19:00 dropoff", ctaLabel: "Add cake" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("Birthday dinner Friday for me + 5 friends");
    s.nodeState("triton", "active");
    // Six Twin nodes — you + five friends — each in lane 1
    s.nodeState("your_twin", "active", "You");
    s.nodeState("partner_twin", "active", "Friend 1");
    s.nodeState("twin_3", "active", "Friend 2");
    s.nodeState("twin_4", "active", "Friend 3");
    s.nodeState("twin_5", "active", "Friend 4");
    s.nodeState("twin_6", "active", "Friend 5");
    s.agentSay("Your Twin",
      "Birthday dinner Friday, six of you. I'll ask each of your friends what they need — just the basics: diet, allergies, what they can spend, what kind of place they like. Nothing else gets shared.",
      "your_twin");
    s.agentSay("Friend 1 Twin",
      "Mine's vegetarian, can spend up to ¥5,000, prefers somewhere lively.",
      "friend_twin");
    s.agentSay("Friend 2 Twin",
      "No allergies, ¥4,000 max, somewhere fun.",
      "friend_twin");
    s.agentSay("Friend 3 Twin",
      "Mine has a shellfish allergy — please be strict about it. Up to ¥4,000, lively vibe.",
      "friend_twin");
    s.agentSay("Friend 4 Twin",
      "Vegetarian, and ¥3,500 is the most they can do this week. Somewhere casual is fine.",
      "friend_twin");
    s.agentSay("Friend 5 Twin",
      "No allergies, ¥5,000, fun atmosphere.",
      "friend_twin");
    s.agentSay("Your Twin",
      "So — two vegetarians, one strict shellfish allergy, and the tightest budget is ¥3,500. I'll aim for around ¥4,000 a head so it works for everyone, and lean toward 'lively' since most asked for that.",
      "your_twin");
    s.edgeFire("your_twin", "pact_broker", "broadcast.scoped");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "Asking the fifty Roppongi and Azabu restaurants. Four can do it.",
      "broker");
    s.pactBroadcast("food.group", 50);
    s.agentSay("Den Roppongi",
      "We can do ¥4,180 a head — separate vegetarian omakase track, no shellfish anywhere in the shared dishes, lively atmosphere.",
      "provider");
    s.pactBid("den_rop", "Den Roppongi", 4_180);
    s.agentSay("Narisawa",
      "We can host all six — ¥18,000 a head, three Michelin stars, all diets handled. I know that's over what you were thinking.",
      "provider");
    s.pactBid("narisawa", "Narisawa", 18_000);
    s.pactDrop("narisawa", "over_budget");
    s.agentSay("Park Restaurant",
      "¥5,200 a head, we have a vegetarian menu. It's a more formal room though.",
      "provider");
    s.pactBid("park_restaurant", "Park Restaurant", 5_200);
    s.agentSay("Friend 2 Twin",
      "Mine specifically asked for 'fun' — Park's a bit too formal.",
      "friend_twin");
    s.pactDrop("park_restaurant", "too_formal");
    s.agentSay("Yakiniku Toraji",
      "¥3,800 a head, meat-focused. Honestly we don't have a real vegetarian option — sorry.",
      "provider");
    s.pactBid("yakiniku_toraji", "Yakiniku Toraji", 3_800);
    s.agentSay("Friend 1 Twin",
      "That rules it out for both of us vegetarians.",
      "friend_twin");
    s.pactDrop("yakiniku_toraji", "no_vegetarian");
    s.agentSay("Friend 3 Twin",
      "Den's the one — they actually have an isolated kitchen for shellfish.",
      "friend_twin");
    s.agentSay("Your Twin",
      "Den fits everyone — two veg, one allergy, all six budgets. Let's book Den.",
      "your_twin");
    s.nodeState("negotiator", "active");
    s.pactSealed("den_rop", "Den Roppongi", 4_180);
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Six wallets just settled in sync. Den's locked, receipt's in everyone's app 🎂",
      "settlement");
    s.nodeState("settlement", "done", "Split paid");
    s.nodeState("midnight", "done", "⛓ 70430d0…");
    s.nodeState("triton", "done");
    s.assistantDone("Den Roppongi's booked — ¥4,180 from each of you. Want me to set up drinks after?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 5. Spring Fashion Drop (Proactive) ────────────────────────────


const fashionDrop: DemoScript = buildDemoScript({
  meta: {
    id: "fashion-drop",
    title: "Spring Fashion Drop",
    subtitle: "Crawler Fleet · Proactive Alert · Aristotle size mapping",
    carrierFit: "both",
    userPrompt: "(proactive — Triton pushes the alert unprompted)",
    consumerId: "demo_consumer",
    turnId: "demo_t_fashion",
    dealId: "demo_d_fashion",
    totalSeconds: 55,
    killerFeatures: ["Crawler Fleet", "Proactive Alert", "Aristotle size mapping (M Uniqlo = L CdG)", "Fashion vertical"],
    endings: {
      receipt: {
        receipt_id: "rcp_fashion_9f0d678",
        action_summary: "Sealed 2 pieces · CdG SS26 trench + structured shirt · 15% loyalty discount",
        midnight_anchor_hex: "9f0d6789e8b8f2305fdba9e0d52b3d7a",
        rationale_steps: [
          "Crawler Fleet · Comme des Garçons SS26 dropped 2h ago",
          "Aristotle size map · M Uniqlo = L CdG (verified across last 4 buys)",
          "Twin trajectory · last 3 buys shifting toward structured silhouettes",
          "Agent negotiated 15% loyalty discount + early-access slot",
        ],
        alternatives_considered: [
          "Deconstructed pants · waited list (Twin: trying new direction, but no fit history at CdG)",
          "Asymmetric coat · color palette miss (Twin: prefers dark/neutral)",
        ],
        jfsa_csv_preview:
          "rcp_fashion_9f0d678,2026-05-18T10:24:00+09:00,deal_sealed,Comme des Garcons,{{carrier.primaryDenomination}},-119000,-119000,9f0d6789e8b8f2305fdba9e0d52b3d7a,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "fashion.silhouette",
        key: "direction",
        old_value: "casual-leaning (last 6mo)",
        new_value: "structured-leaning (last 4 buys all structured)",
        reason: "CdG trench + structured shirt confirms the directional shift Aristotle has been tracking",
        confidence_change: 0.28,
      },
      pivot: {
        headline: "Build the rest of the look?",
        suggestions: [
          { vertical: "fashion.shoes", description: "Margiela Tabi boots · matches structured direction · in your size", ctaLabel: "See pieces" },
          { vertical: "fashion.bag", description: "Lemaire Croissant bag · neutral palette confirmed", ctaLabel: "Show options" },
          { vertical: "service.tailor", description: "Tailor at Aoyama for trench sleeve adjustment", ctaLabel: "Book fitting" },
        ],
      },
    },
  },
  build: (s) => {
    s.nodeState("triton", "active");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "Quick heads-up — Comme des Garçons just dropped their spring collection two hours ago. I was watching for it because of your buying history.",
      "aristotle");
    s.nodeState("your_twin", "active");
    s.agentSay("Your Twin",
      "Looking at your last four fashion purchases — they've all been more structured shapes than your older stuff. There's a clear shift. And from past fits, your usual size M in Uniqlo is a size L in CdG.",
      "your_twin");
    s.agentSay("Aristotle",
      "Three pieces from the new drop match your size and the direction you're heading. Two of them sit inside your usual spend. Want me to start asking around?",
      "aristotle");
    s.agentSay("Your Twin",
      "Yes — try the flagship in Aoyama, Dover Street, and Isetan all at once. The drop sells out fast so let's get an early-access slot if anyone has one.",
      "your_twin");
    s.edgeFire("your_twin", "pact_broker", "broadcast.fashion");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "Asking the three CdG retailers in Tokyo who stocks the pieces and what they can offer.",
      "broker");
    s.pactBroadcast("fashion.cdg", 3);
    s.agentSay("CdG Aoyama",
      "We have both pieces in stock. List is ¥140,000 — we can take 15% off as a loyalty perk, and we have an early-access slot tomorrow at 11.",
      "provider");
    s.pactBid("cdg_flagship", "CdG Aoyama", 119_000);
    s.agentSay("Dover Street Market Ginza",
      "We have them too at ¥140,000. We can't stack the loyalty discount, and it's pickup-only — no slot.",
      "provider");
    s.pactBid("cdg_dover", "Dover Street Market Ginza", 124_000);
    s.agentSay("Isetan Shinjuku",
      "¥140,000 list, and yes we can do the 15% — but for early access you'd need to open one of our store cards first.",
      "provider");
    s.pactBid("cdg_isetan", "Isetan Shinjuku", 119_000);
    s.agentSay("Your Twin",
      "Dover's a no — same price as Aoyama but without the discount or the slot. And we're not opening a store card just for Isetan. Aoyama it is.",
      "your_twin");
    s.pactDrop("cdg_dover", "no_value");
    s.pactDrop("cdg_isetan", "store_card_required");
    s.nodeState("negotiator", "active");
    s.pactSealed("cdg_flagship", "CdG Aoyama", 119_000);
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Settled — ¥119k out, slot held for 11am tomorrow. Enjoy the pickup 👕",
      "settlement");
    s.nodeState("settlement", "done", "Paid ¥119k");
    s.nodeState("midnight", "done", "⛓ 9f0d678…");
    s.nodeState("triton", "done");
    s.assistantDone("Trench and structured shirt locked at CdG Aoyama — ¥119,000, pickup tomorrow at 11. Want me to find shoes to match?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 6. Wellness × Travel Combo ────────────────────────────────────


const wellnessTravel: DemoScript = buildDemoScript({
  meta: {
    id: "wellness-travel",
    title: "Wellness × Travel Combo",
    subtitle: "Cross-vertical · Aristotle adapts your training plan to the Kyoto trip",
    carrierFit: "both",
    userPrompt: "I'm in Kyoto next week for our weekend — keep my marathon training intact",
    consumerId: "demo_consumer",
    turnId: "demo_t_wellness",
    dealId: "demo_d_wellness",
    totalSeconds: 60,
    killerFeatures: ["Cross-vertical intelligence", "Aristotle plan adaptation", "Provider Verifier", "Calendar autoseed"],
    endings: {
      receipt: {
        receipt_id: "rcp_wellness_a8fc3c9",
        action_summary: "Booked 5km loop café Sat 06:00 + nutrition plan adjusted for trip",
        midnight_anchor_hex: "a8fc3c9f2049c22b2459e2a05fdba9e0",
        rationale_steps: [
          "Twin · marathon training plan · long run 18km Saturday",
          "Aristotle · adapt plan for travel · 5km loop near Kyoto ryokan",
          "Provider Verifier · café opens 06:00 (confirmed last week)",
          "Calendar autoseed · synced to Apple Calendar with sleep target 22:00 Friday",
        ],
        alternatives_considered: [
          "Hotel gym (no equipment for tempo run)",
          "Treadmill rental (delivery doesn't reach the ryokan address)",
        ],
        jfsa_csv_preview:
          "rcp_wellness_a8fc3c9,2026-06-13T06:00:00+09:00,concierge_booking,Cafe Bibliotic Hello,{{carrier.primaryDenomination}},-1800,-1800,a8fc3c9f2049c22b2459e2a05fdba9e0,{{carrier.settlementAdapterId}},service_fee",
      },
      twinUpdate: {
        category: "wellness.travel_pattern",
        key: "trip_with_training",
        old_value: "(not yet recorded)",
        new_value: "auto-adapt plan + book early-open breakfast + lock sleep target night-before",
        reason: "First trip-during-training records the cross-vertical adapt pattern Aristotle reuses",
        confidence_change: 0.27,
      },
      pivot: {
        headline: "Inside the rest of the trip?",
        suggestions: [
          { vertical: "wellness.spa", description: "Recovery massage Sunday 16:00 at the ryokan", ctaLabel: "Book 60 min" },
          { vertical: "food.local", description: "Carb-load dinner Friday 19:00 · 4-min walk", ctaLabel: "Reserve table" },
          { vertical: "shopping", description: "New running socks · stock-out alert at your usual store · alternative found", ctaLabel: "Order to ryokan" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("I'm in Kyoto next week — keep my marathon training intact");
    s.nodeState("triton", "active");
    s.nodeState("your_twin", "active");
    s.agentSay("Your Twin",
      "Your plan has an 18km long run on Saturday morning. But you're at a ryokan in Kyoto with your partner. Two things colliding.",
      "your_twin");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "I can adapt the plan — switch the long run to a 5km tempo with a 1km warmup. You'll get the same training effect and it fits a short loop near the ryokan.",
      "aristotle");
    s.agentSay("Your Twin",
      "Good. Then you'll want breakfast at 6am before your partner wakes up — something with real protein, not pastries. And we should get you in bed by 10pm Friday since long-run days always need a proper sleep.",
      "your_twin");
    s.agentSay("Aristotle",
      "Let me find a cafe within walking distance that opens that early and actually has protein on the menu.",
      "aristotle");
    s.edgeFire("aristotle", "pact_broker", "broadcast");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "Six cafes near the ryokan. Three responded.",
      "broker");
    s.pactBroadcast("food.local.early", 6);
    s.agentSay("Cafe Bibliotic Hello",
      "We open at 6. Four-minute walk. Eggs, tofu, protein toast — ¥1,800.",
      "provider");
    s.pactBid("bibliotic", "Cafe Bibliotic Hello", 1_800);
    s.agentSay("Blue Bottle Kyoto",
      "We're ¥2,400 — but honestly we don't open until 7:30. Too late for your run.",
      "provider");
    s.pactBid("blue_bottle_kyoto", "Blue Bottle Kyoto", 2_400);
    s.pactDrop("blue_bottle_kyoto", "opens_too_late");
    s.agentSay("Smart Coffee",
      "We open at 6. ¥1,400. Just pastries though, no protein options.",
      "provider");
    s.pactBid("smart_coffee", "Smart Coffee", 1_400);
    s.agentSay("Your Twin",
      "Smart Coffee is cheaper but pastries before a long run defeats the purpose. Bibliotic's the only real option.",
      "your_twin");
    s.pactDrop("smart_coffee", "no_protein");
    s.agentSay("Aristotle",
      "Agreed — Bibliotic's the only one that opens early enough and serves what you need.",
      "aristotle");
    s.nodeState("negotiator", "active");
    s.pactSealed("bibliotic", "Cafe Bibliotic Hello", 1_800);
    s.edgeFire("negotiator", "midnight", "process.hold");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Reservation locked. Also dropped a note on your calendar: 6am breakfast, 6:30 run start, 10pm bedtime Friday ⏰",
      "settlement");
    s.nodeState("settlement", "done");
    s.nodeState("midnight", "done", "⛓ a8fc3c9…");
    s.nodeState("triton", "done");
    s.assistantDone("Plan adapted, breakfast held, calendar set. Want me to book a recovery massage at the ryokan for Sunday afternoon?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 7. Cross-Marketplace Espresso ─────────────────────────────────


const crossMarketEspresso: DemoScript = buildDemoScript({
  meta: {
    id: "cross-marketplace-espresso",
    title: "Cross-Marketplace Espresso",
    subtitle: "E-commerce vertical · 3-marketplace parallel crawl · authenticity scout",
    carrierFit: "both",
    userPrompt: "New espresso machine · compact, ≤¥80k, good for oat milk",
    consumerId: "demo_consumer",
    turnId: "demo_t_espresso",
    dealId: "demo_d_espresso",
    totalSeconds: 60,
    killerFeatures: ["E-commerce vertical", "Cross-marketplace crawl", "Authenticity scout", "Recurring self-executing agreement"],
    endings: {
      receipt: {
        receipt_id: "rcp_espresso_8627dea",
        action_summary: "Sealed Breville Bambino Plus from Rakuten · ¥64k net after points",
        midnight_anchor_hex: "8627deaf2049c22b2459e2a05fdba9e0",
        rationale_steps: [
          "Amazon JP / Rakuten / Yahoo Shopping crawlers in parallel · 3 candidates",
          "Authenticity scout cross-checked manufacturer serial registry",
          "Provider Verifier dropped 1 candidate (suspected gray-market)",
          "Net cost ranking: Rakuten ¥72k − 8k points = ¥64k beats Amazon ¥78k",
        ],
        alternatives_considered: [
          "Amazon JP listing · genuine but no points apply",
          "Yahoo Shopping listing · flagged counterfeit risk by serial mismatch",
        ],
        jfsa_csv_preview:
          "rcp_espresso_8627dea,2026-05-18T11:42:00+09:00,deal_sealed,Rakuten Marketplace,{{carrier.primaryDenomination}},-64000,-64000,8627deaf2049c22b2459e2a05fdba9e0,{{carrier.settlementAdapterId}},consumption_expense",
      },
      twinUpdate: {
        category: "ecommerce.coffee",
        key: "machine",
        old_value: "(none)",
        new_value: "Breville Bambino Plus · oat-milk capable · compact",
        reason: "First espresso machine purchase seeds the coffee category in your Twin",
        confidence_change: 0.5,
      },
      pivot: {
        headline: "Lock the supply chain?",
        suggestions: [
          { vertical: "ecommerce.recurring", description: "Auto-order beans monthly · pauses when you travel", ctaLabel: "Enable recurring" },
          { vertical: "ecommerce.recurring", description: "Auto-order oat milk weekly", ctaLabel: "Add to recurring" },
          { vertical: "service.maintenance", description: "Annual descale service · scheduled for month 11", ctaLabel: "Add reminder" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("New espresso machine · compact, ≤¥80k, good for oat milk");
    s.nodeState("triton", "active");
    s.nodeState("your_twin", "active");
    s.agentSay("Your Twin",
      "So — compact enough for your counter, under ¥80,000, good with oat milk, and I want to be sure it's the real thing. Let me check Amazon, Rakuten, and Yahoo Shopping all at once.",
      "your_twin");
    s.nodeState("aristotle", "active");
    s.agentSay("Aristotle",
      "Looked through twenty-three listings on Amazon, eighteen on Rakuten, twelve on Yahoo. One model shows up in all three that matches: the Breville Bambino Plus.",
      "aristotle");
    s.edgeFire("aristotle", "pact_broker", "broadcast.ecommerce");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "I'll get the actual offers from each marketplace.",
      "broker");
    s.pactBroadcast("ecommerce.appliance", 3);
    s.agentSay("Amazon JP listing",
      "¥78,000. Genuine, full Japan warranty. We don't have points stacking.",
      "provider");
    s.pactBid("amazon_jp_bambino", "Amazon JP", 78_000);
    s.agentSay("Rakuten listing",
      "¥72,000. Genuine, Japan warranty included. And you have ¥8,000 in points you can put toward it.",
      "provider");
    s.pactBid("rakuten_bambino", "Rakuten", 72_000);
    s.agentSay("Yahoo Shopping listing",
      "¥64,000 — best price on the page.",
      "provider");
    s.pactBid("yahoo_bambino_gray", "Yahoo Shopping", 64_000);
    s.agentSay("Authenticity Scout",
      "Quick check on the Yahoo listing — the serial number isn't in Breville's Japan distribution records. Looks like a gray-market import. No local warranty.",
      "system");
    s.agentSay("Your Twin",
      "That's a hard no — gray market means if it breaks, you're on your own, and there's a real chance the electronics aren't legit.",
      "your_twin");
    s.pactDrop("yahoo_bambino_gray", "gray_market");
    s.agentSay("Negotiator",
      "Comparing the two genuine ones: Amazon is ¥78,000 flat. Rakuten is ¥72,000 minus ¥8,000 points, so ¥64,000 out of pocket. Rakuten saves you ¥14,000.",
      "negotiator");
    s.pactDrop("amazon_jp_bambino", "outpriced_by_rakuten");
    s.nodeState("negotiator", "active");
    s.pactSealed("rakuten_bambino", "Rakuten", 72_000);
    s.edgeFire("negotiator", "midnight", "process.payment");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Settled — Bambino's on the way, Japan warranty active. Enjoy the espresso ☕",
      "settlement");
    s.nodeState("settlement", "done", "Paid ¥64,000");
    s.nodeState("midnight", "done", "⛓ 8627dea…");
    s.nodeState("triton", "done");
    s.assistantDone("Bambino Plus from Rakuten — ¥64,000 after points. Want me to auto-order beans every month?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 8. Hair Salon Auto-Rebook ─────────────────────────────────────


const hairSalonAutoRebook: DemoScript = buildDemoScript({
  meta: {
    id: "hair-salon-autorebook",
    title: "Hair Salon Auto-Rebook",
    subtitle: "Local services · self-executing recurring agreement on Midnight",
    carrierFit: "both",
    userPrompt: "(triggered by recurring agreement — every 6 weeks)",
    consumerId: "demo_consumer",
    turnId: "demo_t_hair",
    dealId: "demo_d_hair",
    totalSeconds: 50,
    killerFeatures: ["Local services vertical", "Recurring self-executing agreement", "Provider Verifier", "Scoped privacy"],
    endings: {
      receipt: {
        receipt_id: "rcp_hair_b9083eb",
        action_summary: "Auto-rebooked Saturday cut · regular stylist booked · alt SHIMA confirmed",
        midnight_anchor_hex: "b9083ebf2049c22b2459e2a05fdba9e0",
        rationale_steps: [
          "Recurring agreement fired · 6 weeks since last cut · Saturday slot needed",
          "Regular stylist · checked first · Saturday fully booked (event)",
          "PACT broadcast to 4 nearby salons matching profile",
          "Twin shared scoped: cut style + eco-friendly products · NOT wellness/diet/budget",
          "Provider Verifier ranked SHIMA · 4.7 reliability over 90 days · eco-certified",
        ],
        alternatives_considered: [
          "SUNNY Aoyama · no eco-friendly products on file",
          "Garden Aoyama · earliest Saturday slot 17:00 (too late)",
          "Mod's Hair · style mismatch flagged by past-fit feedback",
        ],
        jfsa_csv_preview:
          "rcp_hair_b9083eb,2026-05-31T14:00:00+09:00,deal_sealed,SHIMA Harajuku,{{carrier.primaryDenomination}},-9800,-9800,b9083ebf2049c22b2459e2a05fdba9e0,{{carrier.settlementAdapterId}},service_fee",
      },
      twinUpdate: {
        category: "service.hair",
        key: "alt_salon_quality",
        old_value: "(no alt confirmed)",
        new_value: "SHIMA Harajuku · matches eco + cut style · reliability 4.7",
        reason: "Successful alt-salon match · Aristotle ranks SHIMA above the rest for next overflow",
        confidence_change: 0.22,
      },
      pivot: {
        headline: "While you're at SHIMA?",
        suggestions: [
          { vertical: "service.beauty", description: "Eyebrow shaping · 30 min · same salon · same eco brand", ctaLabel: "Add slot" },
          { vertical: "service.massage", description: "Head spa add-on · post-cut · 20 min", ctaLabel: "Add 20 min" },
          { vertical: "shopping.beauty", description: "Eco shampoo restock · pick up after cut", ctaLabel: "Reserve bottle" },
        ],
      },
    },
  },
  build: (s) => {
    s.nodeState("triton", "active");
    s.edgeFire("triton", "midnight", "agreement.lookup");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "your_twin", "twin.scoped");
    s.nodeState("your_twin", "active");
    s.agentSay("Your Twin",
      "I'll only share what's needed — the style you usually get and that you prefer eco-friendly products. Nothing about your budget or schedule, since they don't need that for a booking.",
      "your_twin");
    s.edgeFire("your_twin", "pact_broker", "rebroadcast");
    s.nodeState("pact_broker", "active");
    s.agentSay("MAGNOLIA (your regular)",
      "Hi! Sorry, Saturday's fully booked — there's an industry event. Could you do the following week?",
      "provider");
    s.pactBroadcast("service.hair", 4);
    s.pactBid("regular_stylist", "MAGNOLIA (regular)", 9_500);
    s.pactDrop("regular_stylist", "fully_booked");
    s.agentSay("Your Twin",
      "Next week's too late — you've got a wedding on Friday. Let me ask a few other salons nearby.",
      "your_twin");
    s.agentSay("Marketplace",
      "Asking four salons in the area.",
      "broker");
    s.agentSay("SHIMA Harajuku",
      "Saturday at 2pm works — ¥9,800. We use the eco-certified product line you prefer.",
      "provider");
    s.pactBid("shima", "SHIMA Harajuku", 9_800);
    s.agentSay("SUNNY Aoyama",
      "Saturday 1pm, ¥8,900. We don't carry eco-friendly products though.",
      "provider");
    s.pactBid("sunny_aoyama", "SUNNY Aoyama", 8_900);
    s.agentSay("Garden Aoyama",
      "Our earliest Saturday slot is 5pm. ¥10,500.",
      "provider");
    s.pactBid("garden_aoyama", "Garden Aoyama", 10_500);
    s.agentSay("Mod's Hair",
      "Saturday 2pm, ¥9,200.",
      "provider");
    s.pactBid("mods_hair", "Mod's Hair", 9_200);
    s.agentSay("Your Twin",
      "SUNNY's cheaper but you've been firm on eco-friendly — not going to compromise on that. Garden's too late, you have dinner. And Mod's Hair has come up wrong on two past cuts with clients matching your style. SHIMA's the one.",
      "your_twin");
    s.pactDrop("sunny_aoyama", "no_eco_products");
    s.pactDrop("garden_aoyama", "too_late");
    s.pactDrop("mods_hair", "past_style_mismatch");
    s.agentSay("Negotiator",
      "SHIMA's the only one that gets everything right.",
      "negotiator");
    s.nodeState("negotiator", "active");
    s.pactSealed("shima", "SHIMA Harajuku", 9_800);
    s.edgeFire("negotiator", "midnight", "process.hold");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("{{carrier.settlementBrand}} Wallet",
      "Booked — see you Saturday at 2 ✂️",
      "settlement");
    s.nodeState("settlement", "done");
    s.nodeState("midnight", "done", "⛓ b9083eb…");
    s.nodeState("triton", "done");
    s.assistantDone("Saturday at 2pm at SHIMA, same eco products as your regular. Want me to add an eyebrow shape while you're there?");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


// ── 9. KDDI — Crypto-Native Sushi Night ───────────────────────────


const cryptoSushiNight: DemoScript = buildDemoScript({
  meta: {
    id: "kddi-crypto-sushi",
    title: "Crypto-Native Sushi Night",
    subtitle: "Multi-denom PACT · Coincheck spot lock · JFSA-ready receipt",
    carrierFit: "kddi",
    userPrompt: "Sushi tonight in Ginza · ≤ 0.005 BTC OR ≤ ¥75,000",
    consumerId: "demo_consumer",
    turnId: "demo_t_crypto",
    dealId: "demo_d_crypto",
    totalSeconds: 65,
    killerFeatures: ["UC-8 Coincheck settlement", "Multi-denom PACT", "UC-9 JFSA export", "Rate-lock on Midnight"],
    endings: {
      receipt: {
        receipt_id: "rcp_crypto_0b729e4",
        action_summary: "Sealed Sukiyabashi Jiro · 0.0048 BTC settled via Coincheck spot",
        midnight_anchor_hex: "0b729e4af2049c22b2459e2a05fdba9e0",
        rationale_steps: [
          "Accepted denominations: BTC · JPYC · ETH · YEN · USDC",
          "PACT broadcast · 4 sushi bars bid · 3 in yen · 1 native JPYC",
          "Negotiator ranked by yen-equivalent at locked Coincheck spot rate",
          "Spot rate locked at seal: 1 BTC = ¥15,000,000 · 0.0048 BTC = ¥72,000",
          "Settlement: Coincheck spot order id coincheck_stub:c_47a · Midnight anchored rate",
        ],
        alternatives_considered: [
          "Kyubey Ginza · ¥85k yen (over budget at spot rate)",
          "Sushi Ichi · ¥72k yen but no BTC accepted",
          "Sushi Saito · booked solid through Sunday",
        ],
        jfsa_csv_preview:
          "rcp_crypto_0b729e4,2026-05-18T19:00:00+09:00,deal_sealed,Sukiyabashi Jiro,BTC,-0.0048,-72000,0b729e4af2049c22b2459e2a05fdba9e0,coincheck_spot,consumption_expense",
      },
      twinUpdate: {
        category: "settlement.preference",
        key: "denomination_for_premium_dining",
        old_value: "yen-default",
        new_value: "btc-or-jpyc-preferred when balance ≥ deal · last 3 picks confirm",
        reason: "Third BTC-settled premium dining pick · Aristotle promotes denomination preference",
        confidence_change: 0.31,
      },
      pivot: {
        headline: "Cap the night?",
        suggestions: [
          { vertical: "drinks", description: "Bar High Five · omakase cocktails · accepts JPYC · 4-min walk", ctaLabel: "Reserve" },
          { vertical: "travel.car", description: "Black car home · settles in JPYC · receipt anchored", ctaLabel: "Schedule 22:30" },
          { vertical: "compliance.tax", description: "Run today's transactions through JFSA-ready export now", ctaLabel: "Export CSV" },
        ],
      },
    },
  },
  build: (s) => {
    s.userTypes("Sushi tonight in Ginza · ≤ 0.005 BTC OR ≤ ¥75,000");
    s.nodeState("triton", "active");
    s.nodeState("settlement", "active");
    s.agentSay("au PAY · Coincheck Wallet",
      "Your balances right now: 0.012 BTC, 50,000 JPYC, 200 USDC, plus your au points. I'll lock today's BTC rate for ten minutes — one BTC equals ¥15 million.",
      "settlement");
    s.agentSay("Your Twin",
      "At that rate your 0.005 BTC ceiling is ¥75,000. I'll tell the restaurants you can pay in BTC, JPYC, or yen — whichever they prefer.",
      "your_twin");
    s.edgeFire("triton", "pact_broker", "broadcast.multi_denom");
    s.nodeState("pact_broker", "active");
    s.agentSay("Marketplace",
      "Four sushi bars in Ginza have seats tonight.",
      "broker");
    s.pactBroadcast("food.sushi.premium", 4);
    s.agentSay("Kyubey Ginza",
      "Welcome — two seats at 7pm. ¥85,000. We only take yen though, no crypto.",
      "provider");
    s.pactBid("kyubey", "Kyubey Ginza", 85_000);
    s.agentSay("Sukiyabashi Jiro",
      "Two seats at 7pm — ¥72,000. We accept BTC, JPYC, or yen. Happy to lock at your rate.",
      "provider");
    s.pactBid("jiro_ginza", "Sukiyabashi Jiro", 72_000);
    s.agentSay("Sushi Ichi",
      "¥72,000, two seats at 7pm. Yen only — we're not set up for crypto yet.",
      "provider");
    s.pactBid("sushi_ichi", "Sushi Ichi", 72_000);
    s.agentSay("Sushi Saito",
      "We do accept JPYC — but honestly we're booked solid through Sunday. Sorry.",
      "provider");
    s.pactBid("sushi_saito", "Sushi Saito", 78_000);
    s.agentSay("Your Twin",
      "Kyubey is over budget. Ichi works on price but yen-only — and you prefer to spend BTC when it's an option for the tax records. Saito's not available. That leaves Jiro.",
      "your_twin");
    s.pactDrop("kyubey", "over_budget");
    s.pactDrop("sushi_ichi", "no_crypto");
    s.pactDrop("sushi_saito", "unavailable");
    s.agentSay("Negotiator",
      "Jiro it is. Locking the booking.",
      "negotiator");
    s.nodeState("negotiator", "active");
    s.pactSealed("jiro_ginza", "Sukiyabashi Jiro", 72_000);
    s.edgeFire("negotiator", "midnight", "process.crosschain");
    s.nodeState("midnight", "active");
    s.edgeFire("midnight", "settlement", "deliver");
    s.nodeState("settlement", "active");
    s.agentSay("Coincheck Wallet",
      "0.0048 BTC out, settled. Jiro confirmed. Tax export will show denomination, rate, and yen equivalent come filing time 🍱",
      "settlement");
    s.nodeState("settlement", "done", "Paid 0.0048 BTC");
    s.nodeState("midnight", "done", "⛓ 0b729e4…");
    s.nodeState("triton", "done");
    s.assistantDone("Jiro booked for 7pm — 0.0048 BTC at the locked rate. Tax export's ready whenever you want it.");
    s.endingReceipt();
    s.endingTwinUpdate();
    s.endingPivot();
  },
});


export const DEMO_SCRIPTS: DemoScript[] = [
  kddiWeekendFun,
  agentToAgentLive,
  anniversaryMinato,
  kyotoPartnerWeekend,
  familyHakone,
  groupBirthdayDinner,
  fashionDrop,
  wellnessTravel,
  crossMarketEspresso,
  hairSalonAutoRebook,
  cryptoSushiNight,
];

export function getDemoScript(id: string): DemoScript | undefined {
  return DEMO_SCRIPTS.find((s) => s.id === id);
}
